import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import toast from 'react-hot-toast';
import { authService } from '../services/authService';
import { getUserRoles } from '../../../components/auth/RoleBasedProtectedRoute';

const RoleSelectionPage = () => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const navigate = useNavigate();
  const location = useLocation();

  // Set page title
  useEffect(() => {
    document.title = 'MediFusion - Select Role';
  }, []);

  // Show error message if passed via navigation state
  useEffect(() => {
    if (location.state?.error) {
      toast.error(location.state.error);
    }
  }, [location.state]);

  // Get user data and available roles
  useEffect(() => {
    const currentUser = authService.getCurrentUser();
    if (!currentUser) {
      toast.error('Please login first');
      navigate('/auth/login');
      return;
    }

    setUser(currentUser);
    setIsLoading(false);
  }, [navigate]);

  // Get available roles for the user
  const getAvailableRoles = () => {
    if (!user) return [];

    const userRoles = getUserRoles(user);
    const roleConfigs = {
      admin: { id: 'admin', label: 'Admin', color: 'bg-red-500 hover:bg-red-600' },
      doctor: { id: 'doctor', label: 'Doctor', color: 'bg-blue-500 hover:bg-blue-600' },
      patient: { id: 'patient', label: 'Patient', color: 'bg-green-500 hover:bg-green-600' },
      clinician: { id: 'clinician', label: 'Clinician', color: 'bg-purple-500 hover:bg-purple-600' }
    };

    return userRoles.map(role => roleConfigs[role]).filter(Boolean);
  };

  const handleRoleSelect = (roleId) => {
    // Store selected role in localStorage
    localStorage.setItem('selectedRole', roleId);
    
    // Navigate to appropriate dashboard
    switch (roleId) {
      case 'admin':
        navigate('/admin/dashboard');
        break;
      case 'doctor':
        navigate('/doctor/dashboard');
        break;
      case 'patient':
        navigate('/patient/dashboard');
        break;
      case 'clinician':
        navigate('/clinician/dashboard');
        break;
      default:
        navigate('/dashboard');
    }
  };

  if (isLoading) {
    return (
      <div className="h-screen flex overflow-hidden">
        <div className="w-[45%] relative overflow-hidden h-[115vh]">
          <img
            src="/login_side_image.png"
            alt="Healthcare Professional"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="w-[55%] flex items-center justify-center bg-white">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    );
  }

  const availableRoles = getAvailableRoles();

  // If no roles available, show error
  if (availableRoles.length === 0) {
    return (
      <div className="h-screen flex overflow-hidden">
        <div className="w-[45%] relative overflow-hidden h-[115vh]">
          <img
            src="/login_side_image.png"
            alt="Healthcare Professional"
            className="w-full h-full object-cover"
          />
        </div>
        <div className="w-[55%] flex items-center justify-center bg-white">
          <div className="text-center">
            <h2 className="text-xl font-semibold text-gray-900 mb-2">No Roles Available</h2>
            <p className="text-gray-600 mb-4">You don't have access to any roles. Please contact your administrator.</p>
            <button
              onClick={() => {
                authService.logout();
                navigate('/auth/login');
              }}
              className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-4 rounded-md"
            >
              Back to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="h-screen flex overflow-hidden">
      {/* Left Side - Full Screen Image (45%) */}
      <div className="w-[45%] relative overflow-hidden">
        <img
          src="/login_side_image.png"
          alt="Healthcare Professional"
          className="w-full h-full object-cover"
        />
      </div>

      {/* Right Side - Role Selection (55%) */}
      <div className="w-[55%] flex items-center justify-center bg-white pt-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-xs w-full space-y-6">
          {/* Header */}
          <div className="text-center">
            <h1 className="text-2xl font-semibold text-blue-600 mb-4">
              Welcome To
            </h1>
            
            {/* Medi Fusion Logo */}
            <div className="flex justify-center mb-6">
              <img
                src="/login_medi_logo.png"
                alt="Medi Fusion"
                className="h-16 w-auto"
              />
            </div>
            
            {/* Welcome Message */}
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              Welcome back, {user?.first_name}!
            </h2>
            <p className="text-sm text-gray-600 mb-6">
              Please select your role to continue
            </p>
          </div>

          {/* Role Selection Blocks */}
          <div className="space-y-4">
            {availableRoles.map((role) => (
              <button
                key={role.id}
                onClick={() => handleRoleSelect(role.id)}
                className={`w-full ${role.color} text-white font-medium py-4 px-6 rounded-lg transition-all duration-200 transform hover:scale-105 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 shadow-md hover:shadow-lg`}
              >
                <div className="text-center">
                  <div className="text-lg font-semibold">{role.label}</div>
                </div>
              </button>
            ))}
          </div>

          {/* Logout Option */}
          <div className="text-center pt-4 border-t border-gray-200">
            <button
              onClick={() => {
                authService.logout();
              }}
              className="text-gray-600 hover:text-gray-800 text-sm font-medium"
            >
              Sign Out
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RoleSelectionPage; 